import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { User, Briefcase, Mail, BellRing, CheckCircle2, AlertCircle, MessageCircle, LogOut, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { formatRelative } from "@/lib/relative";

export default function Profile() {
  const { user, loading: authLoading, updateProfile, logout } = useAuth();
  const nav = useNavigate();
  const [name, setName] = useState("");
  const [profession, setProfession] = useState("");
  const [notify, setNotify] = useState(true);
  const [saving, setSaving] = useState(false);
  const [resending, setResending] = useState(false);
  const [threads, setThreads] = useState([]);
  const [replies, setReplies] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (authLoading) return;
    if (!user) { nav("/logga-in", { state: { from: "/profil" } }); return; }
    setName(user.name); setProfession(user.profession); setNotify(user.notify_replies !== false);
    Promise.all([
      api.get("/forum/threads", { params: { author_id: user.user_id } }),
      api.get(`/forum/users/${user.user_id}/replies`),
    ]).then(([t, r]) => { setThreads(t.data); setReplies(r.data); })
      .finally(() => setLoading(false));
  }, [user, authLoading, nav]);

  if (authLoading || !user) {
    return <div className="text-muted-foreground">Laddar…</div>;
  }

  const save = async () => {
    setSaving(true);
    try {
      await updateProfile({ name: name.trim(), profession: profession.trim(), notify_replies: notify });
      toast.success("Profil sparad");
    } catch {
      toast.error("Kunde inte spara");
    } finally { setSaving(false); }
  };

  const resend = async () => {
    setResending(true);
    try {
      const r = await api.post("/auth/resend-verification");
      if (r.data.status === "sent") toast.success("Verifieringsmail skickat – kolla din inkorg.");
      else toast.success("Redan verifierad.");
    } catch { toast.error("Kunde inte skicka"); }
    finally { setResending(false); }
  };

  return (
    <div className="max-w-3xl" data-testid="profile-page">
      <div className="text-[11px] uppercase tracking-[0.25em] text-muted-foreground mb-3 font-mono">
        <span className="title-rule" />Min profil
      </div>
      <div className="flex items-end justify-between mb-7">
        <div className="flex items-center gap-4">
          {user.picture ? (
            <img src={user.picture} alt="" className="size-14 rounded-full border border-border" />
          ) : (
            <div className="size-14 rounded-full bg-primary/10 text-primary flex items-center justify-center font-serif text-xl">
              {(user.name || "?")[0].toUpperCase()}
            </div>
          )}
          <div>
            <h1 className="font-serif text-3xl font-medium tracking-tight">{user.name}</h1>
            <p className="text-sm text-muted-foreground flex items-center gap-2">
              {user.email}
              {user.email_verified ? (
                <Badge variant="outline" className="text-[10px] border-primary/40 text-primary"><CheckCircle2 className="size-3 mr-1" />Verifierad</Badge>
              ) : (
                <Badge variant="outline" className="text-[10px] border-accent/40 text-accent"><AlertCircle className="size-3 mr-1" />Ej verifierad</Badge>
              )}
              {user.role === "admin" && <Badge variant="secondary" className="text-[10px] font-mono">ADMIN</Badge>}
            </p>
          </div>
        </div>
        <Button variant="outline" size="sm" onClick={() => { logout(); nav("/"); }} data-testid="profile-logout">
          <LogOut className="size-4 mr-2" />Logga ut
        </Button>
      </div>

      {!user.email_verified && (
        <Card className="p-4 mb-6 bg-card border-accent/40" data-testid="verify-card">
          <div className="flex items-start gap-3 text-sm">
            <AlertCircle className="size-5 text-accent shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="font-medium mb-0.5">E-post inte verifierad</p>
              <p className="text-xs text-muted-foreground">
                För att kunna posta i forumet behöver du klicka på länken i mailet som skickades till {user.email}.
              </p>
            </div>
            <Button size="sm" variant="outline" onClick={resend} disabled={resending} data-testid="resend-verification">
              {resending ? <Loader2 className="size-3.5 animate-spin mr-2" /> : <Mail className="size-3.5 mr-2" />}
              Skicka igen
            </Button>
          </div>
        </Card>
      )}

      <Card className="p-5 bg-card mb-6">
        <h2 className="font-serif text-lg font-medium mb-4">Inställningar</h2>
        <div className="space-y-3">
          <Field icon={User} label="Namn" value={name} onChange={(e) => setName(e.target.value)} testid="profile-name" />
          <Field icon={Briefcase} label="Yrke / studierollI" value={profession} onChange={(e) => setProfession(e.target.value)} testid="profile-profession" />
          <div className="flex items-center justify-between pt-2 border-t border-border">
            <div className="flex items-center gap-2.5">
              <BellRing className="size-4 text-muted-foreground" />
              <div>
                <p className="text-sm font-medium">E-postnotis vid nya svar</p>
                <p className="text-xs text-muted-foreground">Få mail när någon svarar på dina trådar.</p>
              </div>
            </div>
            <Switch checked={notify} onCheckedChange={setNotify} data-testid="profile-notify" />
          </div>
          <Button onClick={save} disabled={saving} data-testid="profile-save">
            {saving ? <Loader2 className="size-4 animate-spin mr-2" /> : null}Spara
          </Button>
        </div>
      </Card>

      {loading ? (
        <div className="text-muted-foreground text-sm">Laddar mina inlägg…</div>
      ) : (
        <>
          <h2 className="font-serif text-lg font-medium mb-3">
            <span className="title-rule" />Mina trådar ({threads.length})
          </h2>
          {threads.length === 0 ? (
            <p className="text-sm text-muted-foreground mb-6">Du har inte skapat någon tråd än.</p>
          ) : (
            <div className="space-y-2 mb-7" data-testid="my-threads">
              {threads.map((t) => (
                <Link key={t.thread_id} to={`/forum/${t.thread_id}`} className="block">
                  <Card className="p-3 hover:border-accent/60 transition-colors bg-card">
                    <div className="flex items-center gap-3">
                      <div className="flex-1 min-w-0">
                        <div className="font-medium leading-tight line-clamp-1">{t.title}</div>
                        <div className="text-xs text-muted-foreground mt-0.5">{formatRelative(t.created_at)}</div>
                      </div>
                      <Badge variant="secondary" className="font-mono text-[10px]">{t.votes} ⬆</Badge>
                      <span className="text-xs text-muted-foreground flex items-center gap-1"><MessageCircle className="size-3" />{t.reply_count}</span>
                    </div>
                  </Card>
                </Link>
              ))}
            </div>
          )}

          <h2 className="font-serif text-lg font-medium mb-3">
            <span className="title-rule" />Mina svar ({replies.length})
          </h2>
          {replies.length === 0 ? (
            <p className="text-sm text-muted-foreground">Du har inte svarat på någon tråd än.</p>
          ) : (
            <div className="space-y-2" data-testid="my-replies">
              {replies.map((r) => (
                <Link key={r.reply_id} to={`/forum/${r.thread_id}`} className="block">
                  <Card className="p-3 hover:border-accent/60 transition-colors bg-card">
                    <div className="text-xs text-muted-foreground mb-1">på <em className="not-italic text-foreground">{r.thread_title}</em> · {formatRelative(r.created_at)}</div>
                    <p className="text-sm line-clamp-2 text-foreground/85">{r.body}</p>
                  </Card>
                </Link>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}

function Field({ icon: Icon, label, testid, ...props }) {
  return (
    <div>
      <label className="text-[11px] font-mono uppercase tracking-widest text-muted-foreground mb-1 block">{label}</label>
      <div className="relative">
        <Icon className="size-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
        <Input className="pl-9" data-testid={testid} {...props} />
      </div>
    </div>
  );
}
